/**
 * What a Google Search campaign with conversion-based bidding will actually be able to count.
 *
 * Pure: shared by the browser (builder copy) and the execution worker (refusal). The catalog is the
 * account's own `customer_conversion_goal` + enabled `conversion_action` rows, synced by
 * `sync-google-ads`. Campaigns Mora creates use `goal_config_level = CUSTOMER`, so the customer-level
 * goals are the ones that apply (REFERENCES ledger G19-G22).
 */

export type GoogleMeasurementGoal = 'leads' | 'sales' | 'any';

export interface GoogleCustomerConversionGoal {
  category: string;
  origin: string;
  biddable: boolean;
}

export interface GoogleConversionActionEntry {
  id: string;
  name: string;
  category: string;
  origin: string;
  status: string;
  primaryForGoal: boolean;
}

export interface GoogleConversionCatalog {
  customerGoals: readonly GoogleCustomerConversionGoal[];
  conversionActions: readonly GoogleConversionActionEntry[];
}

/**
 * Enum names from ConversionActionCategory (REFERENCES G20). SIGNUP is deliberately not a lead: it is
 * excluded by `sync-google-ads/conversion-categories.ts`, so a SIGNUP action would be measured as ready
 * here while `lead_conversions` never counts it. A guard test keeps the two sets equal.
 */
export const GOOGLE_LEAD_GOAL_CATEGORIES: readonly string[] = [
  'SUBMIT_LEAD_FORM',
  'IMPORTED_LEAD',
  'QUALIFIED_LEAD',
  'CONVERTED_LEAD',
  'PHONE_CALL_LEAD',
  'CONTACT',
  'BOOK_APPOINTMENT',
  'REQUEST_QUOTE',
];

export const GOOGLE_SALES_GOAL_CATEGORIES: readonly string[] = ['PURCHASE'];

/** A catalog older than this is not proof of anything. */
export const GOOGLE_MEASUREMENT_STALE_AFTER_MS = 7 * 24 * 60 * 60 * 1000;

export const GOOGLE_NO_BIDDABLE_GOAL_CODE = 'google_no_biddable_conversion_goal' as const;

export type GoogleCountedGoal = {
  category: string;
  origin: string;
  actionNames: readonly string[];
};

export type GoogleMeasurementStatus =
  | { status: 'ready'; counted: readonly GoogleCountedGoal[] }
  | { status: 'no_conversion_action'; message: string }
  | { status: 'stale_catalog'; fetchedAt: string }
  | { status: 'unread' };

function categoriesFor(goal: GoogleMeasurementGoal): ((category: string) => boolean) | null {
  if (goal === 'leads') return (category) => GOOGLE_LEAD_GOAL_CATEGORIES.includes(category);
  if (goal === 'sales') return (category) => GOOGLE_SALES_GOAL_CATEGORIES.includes(category);
  return null;
}

const GOAL_NOUN: Record<GoogleMeasurementGoal, string> = {
  leads: 'lead conversion',
  sales: 'purchase conversion',
  any: 'conversion',
};

export function googleNoConversionMessage(goal: GoogleMeasurementGoal): string {
  return `This Google Ads account has no enabled ${GOAL_NOUN[goal]} action set to optimise toward. A campaign that maximises conversions would have nothing to count and its results would show 0.`;
}

function parseTime(value: string | null | undefined): number | null {
  if (!value) return null;
  const time = new Date(value).getTime();
  return Number.isFinite(time) ? time : null;
}

export function googleMeasurementStatus(input: {
  goal: GoogleMeasurementGoal;
  catalog: GoogleConversionCatalog | null | undefined;
  fetchedAt: string | null | undefined;
  now?: Date;
}): GoogleMeasurementStatus {
  const fetched = parseTime(input.fetchedAt);
  if (!input.catalog || fetched === null) return { status: 'unread' };
  const now = (input.now ?? new Date()).getTime();
  if (now - fetched > GOOGLE_MEASUREMENT_STALE_AFTER_MS) {
    return { status: 'stale_catalog', fetchedAt: new Date(fetched).toISOString() };
  }
  const inCategory = categoriesFor(input.goal);
  const counted: GoogleCountedGoal[] = [];
  for (const goal of input.catalog.customerGoals) {
    if (!goal.biddable) continue;
    if (inCategory && !inCategory(goal.category)) continue;
    const actions = input.catalog.conversionActions.filter(
      (action) =>
        action.status === 'ENABLED' &&
        action.primaryForGoal &&
        action.category === goal.category &&
        action.origin === goal.origin,
    );
    if (!actions.length) continue;
    counted.push({
      category: goal.category,
      origin: goal.origin,
      actionNames: actions.map((action) => action.name),
    });
  }
  if (!counted.length) {
    return { status: 'no_conversion_action', message: googleNoConversionMessage(input.goal) };
  }
  return { status: 'ready', counted };
}

/** The goal a brief setup intent measures against; `null` for intents that do not bid on conversions. */
export function googleMeasurementGoalForIntent(
  intent: string | null | undefined,
): GoogleMeasurementGoal | null {
  if (intent === 'leads') return 'leads';
  if (intent === 'sales') return 'sales';
  return null;
}

const CONVERSION_BIDDING_KINDS = new Set([
  'maximize_conversions',
  'maximize_conversion_value',
  'target_cpa',
  'target_roas',
]);

export function googleBiddingNeedsConversions(kind: string | null | undefined): boolean {
  return typeof kind === 'string' && CONVERSION_BIDDING_KINDS.has(kind);
}

export type GoogleBiddingGuard =
  | { action: 'allow' }
  | { action: 'warn'; reason: 'unread' | 'stale_catalog' }
  | { action: 'refuse'; code: typeof GOOGLE_NO_BIDDABLE_GOAL_CODE; message: string };

/**
 * Refuses only when a fresh catalog PROVES no biddable goal exists; an unread or stale catalog
 * proves nothing, so it warns. Category-agnostic on purpose: a worker cannot know the brief's goal,
 * and "nothing biddable at all" is the one case that is unambiguously wrong.
 */
export function googleBiddingGuard(input: {
  biddingKind: string | null | undefined;
  catalog: GoogleConversionCatalog | null | undefined;
  fetchedAt: string | null | undefined;
  now?: Date;
}): GoogleBiddingGuard {
  if (!googleBiddingNeedsConversions(input.biddingKind)) return { action: 'allow' };
  const status = googleMeasurementStatus({
    goal: 'any',
    catalog: input.catalog,
    fetchedAt: input.fetchedAt,
    now: input.now,
  });
  if (status.status === 'ready') return { action: 'allow' };
  if (status.status === 'no_conversion_action') {
    return { action: 'refuse', code: GOOGLE_NO_BIDDABLE_GOAL_CODE, message: status.message };
  }
  return { action: 'warn', reason: status.status };
}

function record(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

/** Reads the `jsonb` columns of a catalog row; a malformed row is unread, never a guess. */
export function parseGoogleConversionCatalog(row: {
  customer_goals?: unknown;
  conversion_actions?: unknown;
}): GoogleConversionCatalog | null {
  if (!Array.isArray(row.customer_goals) || !Array.isArray(row.conversion_actions)) return null;
  const customerGoals = row.customer_goals.flatMap((item) => {
    const goal = record(item);
    if (typeof goal.category !== 'string' || typeof goal.origin !== 'string') return [];
    return [{ category: goal.category, origin: goal.origin, biddable: goal.biddable === true }];
  });
  const conversionActions = row.conversion_actions.flatMap((item) => {
    const action = record(item);
    if (typeof action.category !== 'string' || typeof action.origin !== 'string') return [];
    return [
      {
        id: String(action.id ?? ''),
        name: typeof action.name === 'string' ? action.name : 'Unnamed conversion action',
        category: action.category,
        origin: action.origin,
        status: typeof action.status === 'string' ? action.status : 'UNKNOWN',
        primaryForGoal: action.primaryForGoal === true,
      },
    ];
  });
  return { customerGoals, conversionActions };
}
